/* The centre of the workspace: the sheet on its backdrop with rulers, the
   sheet chip (which sheet, its size, what to check), the stale-layout banner
   and the zoom bar. With no sheet yet it shows the drop zone or the build
   prompt instead. */

import { useEffect, useMemo, useState } from 'react';
import { formatDims, formatMoney, requestedCount, isManualDoc, priceSheets } from '@gsb/core';
import { api, type ProjectListItem } from '../api';
import { navigate } from '../router';
import { useStore, build, placeMyself, startGangsheet, setActiveSheet, openPanel, select, setCanvasBg, setFocus, type CanvasBg } from '../store/store';
import { SheetCanvas, type CanvasApi, type View, PX_PER_MM_100 } from '../canvas/SheetCanvas';
import { Icon } from '../ui/icons';
import { MenuButton, MenuItem } from '../ui/primitives';
import { usePreflight, useFlags, useEstimate } from '../ui/Panels';
import { useFileDrop, useFilePicker, useMedia } from '../ui/files';

export function CanvasArea({ onReview }: { onReview: () => void }) {
  const sheets = useStore(s => s.doc.sheets);
  const active = useStore(s => s.activeSheet);
  const stale = useStore(s => s.doc.layoutStale);
  const show = useStore(s => s.show);
  const panMode = useStore(s => s.panMode);
  const canvasBg = useStore(s => s.canvasBg);
  const focus = useStore(s => s.focus);
  const pf = usePreflight();
  const flags = useFlags(pf);
  // where printed artwork overlaps or comes too close on this sheet (small rings on the canvas)
  const spots = useMemo(() => (pf?.issues || []).filter(i => i.sheetIndex === active && i.spots?.length && (i.level === 'error' || i.level === 'warning'))
    .flatMap(i => i.spots!.map(p => ({ ...p, level: i.level as 'error' | 'warning' }))), [pf, active]);
  const [api, setApi] = useState<CanvasApi | null>(null);
  const [view, setView] = useState<View | null>(null);
  const phone = useMedia('(max-width: 759px)');
  const rulers = show.rulers && !phone;
  const sheet = sheets[active];
  return (
    <section className={'canvas-area' + (rulers && sheet ? ' with-rulers' : '')} aria-label="Gangsheet canvas">
      {sheet ? (
        <>
          <SheetCanvas sheetIndex={active} mode="edit" flagged={flags} spots={spots} onApi={setApi} onView={setView} background={canvasBg} refitKey={focus}
            rulers={rulers} edgeGuide={show.guides} showMinimap={show.minimap} panMode={panMode} />
          <SheetChip onReview={onReview} />
          <BlankSheetCard />
          {stale && (
            <div className="stale" role="status"><Icon name="alert" /><span>Your sizes or quantities changed.</span><button className="btn btn-primary btn-xs" onClick={() => void build('build')}>Rebuild</button></div>
          )}
          <ZoomBar api={api} view={view} />
          {sheets.length > 1 && (
            <div className="sheet-nav only-phone">
              <button className="ibtn ibtn-sm" aria-label="Previous sheet" disabled={active === 0} onClick={() => setActiveSheet(active - 1)}><Icon name="chevLeft" /></button>
              <span className="num">{active + 1} / {sheets.length}</span>
              <button className="ibtn ibtn-sm" aria-label="Next sheet" disabled={active >= sheets.length - 1} onClick={() => setActiveSheet(active + 1)}><Icon name="chevRight" /></button>
            </div>
          )}
        </>
      ) : <EmptyCanvas />}
    </section>
  );
}

/* A new gangsheet laid out by hand, with nothing on it yet: what to do first. Artwork added now goes
   straight onto the sheet, which gets longer as more is added. */
function BlankSheetCard() {
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config)!;
  const assets = useStore(s => s.assets);
  const picker = useFilePicker();
  if (!isManualDoc(doc) || doc.sheets.some(sh => sh.items.length)) return null;
  const waiting = doc.designs.some(d => d.assetId.startsWith('pending:') || assets[d.assetId]?.status === 'processing');
  if (doc.designs.length && !waiting) return null;
  const s = doc.sheets[0];
  // still empty, so switching to Auto Build loses nothing: offer it when it can be cheaper
  const bl = cfg.pricing.transfers[doc.transfer].byLength;
  const firstMetre = priceSheets([{ kind: 'roll', widthMm: bl.widthMm, lengthMm: bl.minLengthMm }], doc.transfer, cfg.pricing as never).total;
  const cheaper = [...cfg.pricing.transfers[doc.transfer].fixedSheets].sort((a, b) => a.price - b.price).find(f => f.price < firstMetre) || null;
  return (
    <div className="blank-card" role="region" aria-label="Start your sheet">
      <span className="drop-ico">{waiting ? <span className="spin" /> : <Icon name="sheets" />}</span>
      <h2>{waiting ? 'Adding your artwork…' : 'Your ' + formatDims(s.widthMm, s.lengthMm, 'cm') + ' sheet is ready'}</h2>
      <p className="muted small">{waiting
        ? 'Each design goes onto the sheet as soon as we’ve checked it.'
        : 'Add your artwork and it goes straight onto the sheet. Drag it where you like; the sheet gets longer as you add more.'}</p>
      {!waiting && (
        <div className="row-btns" style={{ justifyContent: 'center' }}>
          <button type="button" className="btn btn-primary" onClick={picker.open}><Icon name="upload" />Choose files</button>
          <button type="button" className="btn btn-line" onClick={() => openPanel('text')}><Icon name="type" />Add text</button>
        </div>
      )}
      {picker.input}
      {!waiting && <span className="tiny muted">Or drop files anywhere. PNG with a transparent background prints best.</span>}
      {!waiting && cheaper && (
        <p className="blank-price small">
          This sheet is charged by the metre, from {formatMoney(firstMetre)} ({bl.minLengthMm / 1000} m minimum). Only a few small designs?{' '}
          <button type="button" className="link small" onClick={() => startGangsheet('auto', doc.transfer)}>Use Auto Build instead</button>: it can fit them on {/^[AEIOU]/.test(cheaper.name) ? 'an' : 'a'} {cheaper.name} for {formatMoney(cheaper.price)}.
        </p>
      )}
    </div>
  );
}

/* Which sheet, its size and kind, and a badge for anything to check. */
function SheetChip({ onReview }: { onReview: () => void }) {
  const sheets = useStore(s => s.doc.sheets);
  const active = useStore(s => s.activeSheet);
  const unit = useStore(s => s.doc.settings.unit);
  const fixed = useStore(s => s.config?.pricing.transfers[s.doc.transfer].fixedSheets);
  const pf = usePreflight();
  const s = sheets[active];
  if (!s) return null;
  const kind = s.kind === 'fixed' ? (fixed?.find(f => f.key === s.fixedKey)?.name || 'Fixed sheet') : s.kind === 'roll' ? 'By the metre' : 'Set length';
  // (an empty gangsheet isn't something to fix yet: the empty sheet says what to do)
  const mine = pf?.issues.filter(i => (i.level === 'error' || i.level === 'warning') && i.code !== 'empty' && (i.sheetIndex === undefined || i.sheetIndex === active)) || [];
  const errs = mine.filter(i => i.level === 'error').length;
  const showIssues = () => {
    const ids = mine.flatMap(i => i.itemIds || []);
    if (ids.length) select([...new Set(ids)]);
    onReview();
  };
  return (
    <div className="sheet-chip">
      <span className="sc-main">
        {sheets.length > 1 && <button className="ibtn ibtn-xs no-phone" aria-label="Previous sheet" disabled={active === 0} onClick={() => setActiveSheet(active - 1)}><Icon name="chevLeft" /></button>}
        <b>Sheet {active + 1}{sheets.length > 1 ? <span className="muted"> of {sheets.length}</span> : null}</b>
        {sheets.length > 1 && <button className="ibtn ibtn-xs no-phone" aria-label="Next sheet" disabled={active >= sheets.length - 1} onClick={() => setActiveSheet(active + 1)}><Icon name="chevRight" /></button>}
        <span className="sc-sep" />
        <span className="num">{formatDims(s.widthMm, s.lengthMm, unit === 'mm' ? 'cm' : unit)}</span>
        <span className="muted no-phone">· {kind} · {s.items.length} transfer{s.items.length === 1 ? '' : 's'}</span>
      </span>
      {mine.length > 0
        ? <button className={'sc-warn' + (errs ? ' is-bad' : '')} onClick={showIssues}><Icon name="alert" />{mine.length} to check</button>
        : s.items.length > 0 && <span className="sc-ok"><Icon name="okCircle" /><span className="no-phone">Ready to print</span></span>}
    </div>
  );
}

function ZoomBar({ api, view }: { api: CanvasApi | null; view: View | null }) {
  const panMode = useStore(s => s.panMode);
  const focus = useStore(s => s.focus);
  const pct = view ? Math.round((view.scale / PX_PER_MM_100) * 100) : 100;
  return (
    <div className="zoombar" role="toolbar" aria-label="Zoom">
      <button className="ibtn" aria-pressed={!panMode} aria-label="Select and move" data-tip-pos="up" data-tip="Select and move (V)" onClick={() => useStore.setState({ panMode: false })}><Icon name="select" /></button>
      <button className="ibtn" aria-pressed={panMode} aria-label="Pan" data-tip-pos="up" data-tip="Pan (H, or hold Space)" onClick={() => useStore.setState({ panMode: true })}><Icon name="hand" /></button>
      <span className="zb-sep" />
      <button className="ibtn" aria-label="Zoom out" data-tip-pos="up" data-tip="Zoom out" onClick={() => api?.zoomBy(1 / 1.25)}><Icon name="zoomOut" /></button>
      <MenuButton up button={(open, toggle) => <button className="pct num" aria-expanded={open} onClick={toggle} aria-label="Zoom level">{pct}%</button>}>{close => (
        <>
          <MenuItem icon="fit" label="Fit sheet" onClick={() => { api?.fit(); close(); }} />
          <MenuItem icon="zoomIn" label="100% (real size)" onClick={() => { api?.actual(); close(); }} />
          <hr /><p className="tiny muted" style={{ padding: '4px 10px 8px', maxWidth: 230 }}>Ctrl/⌘ + scroll to zoom. Scroll to move along the sheet. Hold Space and drag to pan.</p>
        </>
      )}</MenuButton>
      <button className="ibtn" aria-label="Zoom in" data-tip-pos="up" data-tip="Zoom in" onClick={() => api?.zoomBy(1.25)}><Icon name="zoomIn" /></button>
      <button className="ibtn" aria-label="Fit sheet" data-tip-pos="up" data-tip="Fit to screen" onClick={() => api?.fit()}><Icon name="fit" /></button>

      <span className="zb-sep" />
      <BackgroundMenu />
      <button className="ibtn" aria-label={focus ? 'Exit focus mode' : 'Focus mode'} aria-pressed={focus} data-tip-pos="up" data-tip={focus ? 'Exit focus mode (F)' : 'Focus mode: hide the panels (F)'} onClick={() => setFocus(!focus)}><Icon name={focus ? 'unfocus' : 'focus'} /></button>
    </div>
  );
}

export const BACKGROUNDS: { value: CanvasBg; label: string; hint: string }[] = [
  { value: 'checker', label: 'Film', hint: 'See-through, like the film' },
  { value: 'dark', label: 'Dark', hint: 'How it looks on a dark garment' },
  { value: 'white', label: 'White', hint: 'How it looks on a white garment' }
];

/* What the film looks like on screen. Only the view: the print file is always transparent. */
function BackgroundMenu() {
  const bg = useStore(s => s.canvasBg);
  const cur = BACKGROUNDS.find(b => b.value === bg) || BACKGROUNDS[0];
  return (
    <MenuButton up align="right" button={(open, toggle) => (
      <button className="ibtn bg-btn" aria-label={'Background: ' + cur.label} aria-expanded={open} onClick={toggle} data-tip-pos="up" data-tip="Background (view only)">
        <span className={'swatch sw-' + bg} aria-hidden="true" />
      </button>
    )}>{close => (
      <>
        <p className="mi-h">Background</p>
        {BACKGROUNDS.map(b => (
          <button key={b.value} type="button" className="mi" role="menuitemradio" aria-checked={bg === b.value} onClick={() => { setCanvasBg(b.value); close(); }}>
            <span className={'swatch sw-' + b.value} aria-hidden="true" />
            <span className="grow"><b>{b.label}</b><small>{b.hint}</small></span>
            {bg === b.value && <Icon name="check" />}
          </button>
        ))}
        <hr /><p className="tiny muted" style={{ padding: '4px 10px 8px', maxWidth: 240 }}>Only changes the view. Your print file always prints just the artwork, on clear film.</p>
      </>
    )}</MenuButton>
  );
}

/* No sheet yet: a big, obvious place to start (the whole card opens the file
   picker), how it works in three steps, then the build prompt once designs are in. */
function EmptyCanvas() {
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config)!;
  const assets = useStore(s => s.assets);
  const panel = useStore(s => s.panel);
  const est = useEstimate();
  const drop = useFileDrop();
  const picker = useFilePicker();
  const transfers = requestedCount(doc);
  const waiting = doc.designs.filter(d => d.assetId.startsWith('pending:') || assets[d.assetId]?.status === 'processing').length;
  const tp = cfg.pricing.transfers[doc.transfer];
  const widthCm = Math.round(tp.byLength.widthMm / 10);
  if (!doc.designs.length) {
    return (
      <div className="empty">
        <div className={'drop drop-hero' + (drop.over ? ' is-over' : '')} {...drop.bind}
          onClick={e => { if (!(e.target as HTMLElement).closest('button, a, input')) picker.open(); }}>
          <span className="drop-ico"><Icon name="upload" /></span>
          <h1>Upload your artwork to start</h1>
          <p className="muted">Drag your files here, or choose them from your device. We check the print quality and trim any empty space.</p>
          <div className="row-btns" style={{ justifyContent: 'center' }}>
            <button type="button" className="btn btn-primary btn-lg" onClick={picker.open}><Icon name="upload" />Choose files</button>
            <button type="button" className="btn btn-line btn-lg" onClick={() => openPanel('text')} data-tip="Names, numbers or a slogan"><Icon name="type" />Add text</button>
          </div>
          {picker.input}
          <span className="types">PNG with a transparent background prints best · JPG, WEBP, SVG, PDF and TIFF work too · up to {cfg.builder.upload.maxFileMb} MB each</span>
        </div>
        <ol className="how" aria-label="How it works">
          <li><b className="how-n">1</b><span><strong>Upload</strong> your artwork, or add text</span></li>
          <li><b className="how-n">2</b><span><strong>Set the size</strong> and how many you need</span></li>
          <li><b className="how-n">3</b><span><strong>Build &amp; order</strong>: we lay it out to use the least film</span></li>
        </ol>
        <ul className="assure assure-row">
          <li><Icon name="check" />{widthCm} cm print width</li>
          {tp.fixedSheets.length > 0 && <li><Icon name="check" />Sheets from {formatMoney(Math.min(...tp.fixedSheets.map(f => f.price)))}</li>}
          <li><Icon name="check" />No minimums</li>
          <li><Icon name="check" />300 DPI print file</li>
        </ul>
        <Recent />
      </div>
    );
  }
  return (
    <div className="empty">
      <div className="ready-card">
        <span className="drop-ico">{waiting ? <span className="spin" /> : <Icon name="build" />}</span>
        <h1>{waiting ? 'Uploading your designs…' : 'Ready to build your gangsheet'}</h1>
        <p className="muted">
          <b className="num">{transfers}</b> transfer{transfers === 1 ? '' : 's'} from {doc.designs.length} design{doc.designs.length === 1 ? '' : 's'}
          {est ? <> · about {est.lengthMm ? (est.lengthMm / 1000).toFixed(1) + ' m · ' : ''}<b className="num">{formatMoney(est.total)}</b></> : null}
        </p>
        <div className="row-btns" style={{ justifyContent: 'center' }}>
          <button className="btn btn-primary btn-lg" disabled={!transfers || waiting > 0} onClick={() => void build('build')}><Icon name="build" />Build my gangsheet</button>
          <button className="btn btn-line btn-lg" onClick={() => void placeMyself()} data-tip="Put them on a by-the-metre sheet and arrange them yourself"><Icon name="plus" />Place them myself</button>
        </div>
        <p className="tiny muted">
          {doc.settings.sheet.mode === 'auto' ? 'We arrange everything to use the least film and pick the cheapest sheet.' : 'We arrange everything to use the least film, on the sheet you chose.'} You can move things around after.
          {panel !== 'designs' && <> <button className="link tiny" onClick={() => openPanel('designs')}>Check sizes and quantities</button></>}
        </p>
      </div>
    </div>
  );
}

/* Saved gang sheets in this browser, to carry on with. */
function Recent() {
  const pid = useStore(s => s.projectId);
  const [list, setList] = useState<ProjectListItem[]>([]);
  useEffect(() => { if (!pid) api.list().then(r => setList(r.projects.slice(0, 3))).catch(() => {}); }, [pid]);
  if (!list.length) return null;
  return (
    <div className="recent">
      <span className="label">Carry on where you left off</span>
      <div className="recent-row">
        {list.map(p => (
          <a key={p.id} className="resume" href={'/p/' + p.id} onClick={e => { e.preventDefault(); navigate('/p/' + p.id); }}>
            <span className="th"><img src={p.thumbUrl} alt="" loading="lazy" onError={e => ((e.target as HTMLImageElement).style.display = 'none')} /></span>
            <span className="grow"><b>{p.name}</b><small>{p.summary.sheets?.length ? p.summary.sheets.map(s => formatDims(s.widthMm, s.lengthMm, 'cm')).join(' + ') : p.summary.designs + ' design' + (p.summary.designs === 1 ? '' : 's')}{p.summary.price != null ? ' · ' + formatMoney(p.summary.price) : ''}</small></span>
            <Icon name="chevRight" />
          </a>
        ))}
      </div>
      <button className="link small" onClick={() => navigate('/my')}>All my gang sheets <Icon name="arrowRight" /></button>
    </div>
  );
}
